"use client";

import * as React from "react";
import Link from "next/link";
import { Clapperboard, Play } from "lucide-react";
import type { Video } from "@gerama/types";
import { usePlayerStore } from "@gerama/stores";
import { formatDuration, formatNumber } from "@gerama/utils";
import { MediaImage as Image } from "@/components/media-image";
import { artistPath } from "@/features/artists/artist-path";
import { publicPath } from "@/lib/public-path";

/** 16:9 video tile: cover, duration badge, title, artists and views. */
/** `contextLabel`/`contextHref` say where the click came from — the player shows "playing from …". */
function VideoCardImpl({
  video,
  contextLabel,
  contextHref,
  priority,
}: {
  video: Video;
  contextLabel?: string;
  contextHref?: string;
  /** Above the fold: skip lazy loading (LCP). */
  priority?: boolean;
}) {
  return (
    <div className="group min-w-0">
      <Link
        href={publicPath("videos", video)}
        className="block"
        onClick={() =>
          usePlayerStore
            .getState()
            .setVideoContextHint(contextLabel ? { label: contextLabel, href: contextHref } : null)
        }
      >
        <div className="bg-muted relative aspect-video overflow-hidden rounded-lg shadow-md shadow-black/30">
          {video.cover ? (
            <Image
              src={video.cover}
              alt=""
              fill
              sizes="(min-width: 1024px) 30vw, 90vw"
              priority={priority}
              className="object-cover transition-transform duration-300 group-hover:scale-105"
            />
          ) : (
            <Clapperboard className="text-muted-foreground absolute inset-0 m-auto size-8" />
          )}
          <span className="absolute inset-0 flex items-center justify-center bg-black/0 transition-colors group-hover:bg-black/30">
            <Play className="size-10 fill-white text-white opacity-0 drop-shadow transition-opacity group-hover:opacity-100" />
          </span>
          {video.duration > 0 && (
            <span
              dir="ltr"
              className="absolute end-2 bottom-2 rounded bg-black/70 px-1.5 py-0.5 text-[11px] font-medium text-white tabular-nums"
            >
              {formatDuration(video.duration)}
            </span>
          )}
        </div>
        <p className="mt-2 truncate text-sm font-semibold">{video.name}</p>
      </Link>
      <p className="text-muted-foreground truncate text-xs">
        {video.artists?.length ? (
          video.artists.map((artist, i) => (
            <span key={artist.id}>
              {i > 0 && "، "}
              <Link
                href={artistPath(artist)}
                className="hover:text-foreground hover:underline"
              >
                {artist.name}
              </Link>
            </span>
          ))
        ) : (
          <>{formatNumber(video.playCount, "fa")} بازدید</>
        )}
      </p>
    </div>
  );
}

/** Memoised: a list re-renders when a page is appended, not every row with it. */
export const VideoCard = React.memo(VideoCardImpl);
